/**
 * Outbound link decoration for the booking app (velto.podplay.app).
 *
 * A Google Ads click lands on veltopb.com carrying ?gclid= (or gbraid / wbraid
 * on iOS app + web-to-app campaigns). Booking happens on a *different* domain,
 * so unless those params are copied onto the outbound href the conversion is
 * never tied back to the ad and the campaign looks dead. Same story for utm_*
 * on any hand-tagged campaign.
 *
 * This is link decoration only — no cookies, no network, no analytics. The
 * event layer lives in src/lib/tracking.ts and is deliberately untouched here.
 *
 * The params are kept in sessionStorage for the visit, so a visitor who lands
 * from an ad, reads another page and then clicks Book still carries them.
 */

/** Ad-platform click IDs forwarded verbatim. utm_* is matched by prefix below. */
const CLICK_IDS = ["gclid", "gbraid", "wbraid"];

const isForwarded = (key: string) =>
  CLICK_IDS.includes(key) || key.startsWith("utm_");

const STORE_KEY = "velto_attribution";

/** Remember the forwarded params from this page's URL for the rest of the visit. */
function remember(search: string): void {
  const keep = new URLSearchParams();
  new URLSearchParams(search).forEach((value, key) => {
    if (isForwarded(key)) keep.append(key, value);
  });
  const s = keep.toString();
  if (!s) return;
  try {
    window.sessionStorage.setItem(STORE_KEY, s);
  } catch {
    /* storage blocked: fall back to the live URL only */
  }
}

function stored(): string {
  try {
    return window.sessionStorage.getItem(STORE_KEY) || "";
  } catch {
    return "";
  }
}

/**
 * The current query string plus any attribution params kept from earlier in
 * the visit, or "" when there is no browser. During the puppeteer prerender,
 * routes are crawled without a query string and storage starts empty, so
 * decorated hrefs come out byte-identical to plain ones in the static HTML.
 */
export function currentSearch(): string {
  if (typeof window === "undefined") return "";
  const live = window.location.search;
  remember(live);
  const kept = stored();
  if (!kept) return live;
  // The live URL wins; kept params only fill in what it lacks.
  const merged = new URLSearchParams(live);
  new URLSearchParams(kept).forEach((value, key) => {
    if (!merged.has(key)) merged.append(key, value);
  });
  const q = merged.toString();
  return q ? `?${q}` : "";
}

/**
 * Copy the attribution params from the current page URL onto `href`.
 *
 * Merges with any query the href already carries and never duplicates a key:
 * a param written into the href by hand always wins over the forwarded one.
 * Only absolute http(s) URLs and root-relative paths are touched — "#faqs",
 * "mailto:", and "tel:" hrefs are returned untouched.
 */
export function withAttribution(href: string, search: string = currentSearch()): string {
  if (!search) return href;
  if (!/^https?:\/\//i.test(href) && !href.startsWith("/")) return href;

  const hashAt = href.indexOf("#");
  const base = hashAt === -1 ? href : href.slice(0, hashAt);
  const hash = hashAt === -1 ? "" : href.slice(hashAt);

  const queryAt = base.indexOf("?");
  const path = queryAt === -1 ? base : base.slice(0, queryAt);
  const params = new URLSearchParams(queryAt === -1 ? "" : base.slice(queryAt + 1));

  let added = false;
  new URLSearchParams(search).forEach((value, key) => {
    if (!isForwarded(key) || params.has(key)) return;
    params.append(key, value);
    added = true;
  });
  // Nothing to forward: return the original string so its encoding is preserved
  // exactly rather than round-tripped through URLSearchParams.
  if (!added) return href;

  const query = params.toString();
  return path + (query ? `?${query}` : "") + hash;
}
